/* mg-tumbler-form.js: the order form for the Custom Engraved 40 oz Tumbler.
 *
 * Contract: TUMBLER-SCOPE.md, "BUILD CONTRACT, 2026-09-24 evening".
 *
 *   1. The customer picks a color, how many, and what goes on the front (and
 *      on the back, +$5.00 a tumbler): engraved text in one of 12 fonts, an
 *      engraved logo, or a full color image (UV DTF, applied by hand).
 *   2. Logo and full color files upload the moment they are picked, to the
 *      same POST /apps/design/api/upload the designer uses (raw file body,
 *      answers {key, url, ...}).
 *   3. "Add to cart" checks everything here first, then sends the choices
 *      to the app's door, POST /apps/design/api/cart/tumbler, which checks
 *      them again, stores the design and answers with `items`.
 *   4. Those `items` go to /cart/add.js AS IS. The server writes every line
 *      property; the theme never invents one.
 *
 * Every refusal is said in words, next to its field AND in the alert box
 * above the button (standing rule: silent refusals read as bugs). A door
 * refusal shows its `detail`; a Shopify refusal its `description` or
 * `message`.
 *
 * The pure half (prices, text rules, the payload, error wording) sits at the
 * top and is exported under node, so backend/tests/js/tumbler_theme_probe.js
 * runs this shipped file rather than a copy of it.
 */
(function () {
  'use strict';

  // ------------------------------------------------------------ the rules

  var MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // backend engine/image_probe.MAX_BYTES
  var FILE_EXT_RE = /\.(png|jpe?g|webp|svg|pdf|ai)$/i;
  var FILE_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/svg+xml',
    'application/pdf',
    'application/postscript',
    'application/illustrator'
  ];
  // Tabs and other control characters (a newline is how lines are made).
  var CONTROL_RE = new RegExp('[\\u0000-\\u0009\\u000B-\\u001F\\u007F-\\u009F\\u2028\\u2029]');
  var EMOJI_RE = emojiPattern();

  function emojiPattern() {
    // (c), (r) and TM are "pictographic" to Unicode but are ordinary marks on
    // a nameplate, so they are left to the server to judge.
    try {
      return new RegExp('(?![\\u00A9\\u00AE\\u2122])[\\p{Extended_Pictographic}\\u200D\\uFE0F\\u20E3]', 'u');
    } catch (e) {
      return new RegExp('[\\uD83C-\\uDBFF][\\uDC00-\\uDFFF]|[\\u2600-\\u27BF]');
    }
  }

  function isUploadMethod(method) {
    return method === 'engraved_logo' || method === 'full_color';
  }

  function toCents(n) {
    var x = Number(n);
    return isFinite(x) ? Math.round(x * 100) : 0;
  }

  function money(cents) {
    var n = Math.round(Number(cents) || 0);
    var sign = n < 0 ? '-' : '';
    n = Math.abs(n);
    var whole = String(Math.floor(n / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var part = n % 100;
    return sign + '$' + whole + '.' + (part < 10 ? '0' : '') + part;
  }

  function plural(n, one, many) {
    return n === 1 ? one : many;
  }

  // The config's ladder, cleaned and in order: [{min, max (null = no top), unit}].
  function sortedLadder(ladder) {
    return (Array.isArray(ladder) ? ladder : [])
      .filter(function (r) {
        return r && isFinite(Number(r.min)) && isFinite(Number(r.unit)) && Number(r.unit) > 0;
      })
      .map(function (r) {
        var top = r.max === null || r.max === undefined || r.max === '' ? null : Number(r.max);
        return { min: Number(r.min), max: isFinite(top) ? top : null, unit: Number(r.unit) };
      })
      .sort(function (a, b) {
        return a.min - b.min;
      });
  }

  function rungIndex(ladder, count) {
    for (var i = 0; i < ladder.length; i++) {
      var r = ladder[i];
      if (count >= r.min && (r.max === null || count <= r.max)) return i;
    }
    if (!ladder.length) return -1;
    return count < ladder[0].min ? 0 : ladder.length - 1;
  }

  function rungLabel(r) {
    if (r.max === null) return r.min + ' or more';
    if (r.max === r.min) return String(r.min);
    return r.min + ' to ' + r.max;
  }

  // The live price lines. `inCart` is how many tumblers the cart already
  // holds: the group price counts every tumbler in the cart together, so the
  // rung is found from the cart's tumblers plus these.
  function priceLines(cfg, qty, withBack, inCart) {
    var out = { tumblers: '', back: '', total: '', cart: '', next: '', rung: -1 };
    var ladder = sortedLadder(cfg && cfg.ladder);
    if (!ladder.length || !(qty >= 1) || Math.floor(qty) !== qty) return out;
    var extra = Math.max(0, parseInt(inCart, 10) || 0);
    var count = qty + extra;
    var i = rungIndex(ladder, count);
    var unit = toCents(ladder[i].unit);
    var backEach = toCents(cfg.back_side_price);
    var sum = unit * qty;
    out.rung = i;
    out.tumblers = qty + ' ' + plural(qty, 'tumbler', 'tumblers') + ' at ' + money(unit) + ' each: ' + money(sum);
    if (withBack) {
      out.back = 'Back side, ' + qty + ' at ' + money(backEach) + ': ' + money(backEach * qty);
      sum += backEach * qty;
    }
    out.total = 'Total: ' + money(sum);
    if (extra) {
      out.cart =
        'Priced with the ' + extra + ' ' + plural(extra, 'tumbler', 'tumblers') +
        ' already in your cart (' + count + ' in all).';
    }
    if (!cfg.fallback) {
      var nxt = ladder[i + 1];
      if (nxt && nxt.min > count) {
        out.next = extra
          ? 'Add ' + (nxt.min - count) + ' more and each tumbler is ' + money(toCents(nxt.unit)) + '.'
          : 'Order ' + nxt.min + ' or more and each tumbler is ' + money(toCents(nxt.unit)) + '.';
      } else if (ladder.length > 1 && i === ladder.length - 1) {
        out.next = 'You are getting our best group price.';
      }
    }
    return out;
  }

  // Engraved text: 1 to max_lines lines, at most max_chars characters a line,
  // no emoji or control characters. Blank lines before and after the text are
  // not part of it; each line's own leading and trailing spaces neither.
  function textCheck(raw, limits, sideWord) {
    var maxLines = (limits && Number(limits.max_lines)) || 3;
    var maxChars = (limits && Number(limits.max_chars)) || 40;
    var lines = String(raw || '')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map(function (line) {
        return line.trim();
      });
    while (lines.length && !lines[0]) lines.shift();
    while (lines.length && !lines[lines.length - 1]) lines.pop();
    var text = lines.join('\n');
    var counts = lines.map(function (line) {
      return Array.from(line).length;
    });
    var where = sideWord ? ' for the ' + sideWord : '';
    var problems = [];
    if (!lines.length) {
      problems.push('Please type the text' + where + '.');
    } else {
      if (CONTROL_RE.test(text)) {
        problems.push('Please remove the tabs or special characters from the text' + where + '.');
      }
      if (EMOJI_RE.test(text)) {
        problems.push("Emoji can't be engraved. Please remove them from the text" + where + '.');
      }
      if (lines.length > maxLines) {
        problems.push('Please use ' + maxLines + ' lines or fewer' + where + '. You have ' + lines.length + '.');
      }
      counts.forEach(function (n, k) {
        if (n > maxChars) {
          problems.push(
            'Line ' + (k + 1) + (sideWord ? ' of the ' + sideWord : '') + ' has ' + n +
            ' characters. Please keep each line to ' + maxChars + ' or fewer.'
          );
        }
      });
    }
    return { text: text, lines: lines, counts: counts, maxLines: maxLines, maxChars: maxChars, problems: problems };
  }

  // The form's words that count lines, characters and fonts, from the
  // config (the owner edits them in the app, 2026-09-27). With the shipped
  // 3 lines, 40 characters and 12 fonts they are the snippet's own words.
  // Null when the numbers are missing, so the snippet's words stay.
  function textWords(limits, fontCount) {
    var lines = limits ? Number(limits.max_lines) : 0;
    var chars = limits ? Number(limits.max_chars) : 0;
    if (!(lines >= 1) || !(chars >= 1)) return null;
    var fonts = Number(fontCount) || 0;
    var lineWord = lines === 1 ? ' line' : ' lines';
    return {
      about:
        'Up to ' + lines + lineWord +
        (fonts === 1 ? ' in our engraving font.' : fonts > 1 ? ' in one of our ' + fonts + ' fonts.' : '.'),
      rule: 'Your text: ' + (lines === 1 ? '1 line' : '1 to ' + lines + ' lines') + ', up to ' + chars + ' characters a line'
    };
  }

  // Each color's chip from the config's color_hex ({name: "#rrggbb"}), for
  // a color the owner added or renamed in the app. Anything that is not a
  // six digit color code is left out, so the theme's own chip stays.
  function chipColors(map) {
    var out = {};
    if (!map || typeof map !== 'object') return out;
    Object.keys(map).forEach(function (name) {
      var hex = map[name];
      if (typeof hex === 'string' && /^#[0-9a-fA-F]{6}$/.test(hex)) out[name] = hex;
    });
    return out;
  }

  // ---- the picture's quality reading and its words (owner 2026-10-07)
  //
  // An uploaded picture is graded the way the order's production ticket
  // grades it: its pixels over the size it prints at (the full color print
  // rule, uvdtf_gangsheet._fit: the width, unless that is taller than the
  // tallest, then the tallest) against the same DPI floor (engine
  // apparel_print.MIN_DPI). The floor and the size come with the config
  // (cfg.quality), so the notice and the ticket cannot disagree. The bands
  // are the designer's quality meter's: twice the floor excellent, the floor
  // good, 60 percent of it fair, less low. Only Fair and Low say anything.
  // A vector file, a PDF or a picture with no pixel size has no reading.
  //
  // The words are the shop's own (the admin's tumbler page, picture_words);
  // these shipped ones are the moment before the config arrives and a
  // fallback per word, and match data/tumblers.json.
  var PICTURE_WORDS = {
    quality_title: 'Print quality',
    quality_fair: 'Fair',
    quality_low: 'Low',
    low_res_fair:
      'At {size} this picture prints at {dpi} DPI, a little under the {need} DPI we like, so it may look a little soft. A bigger picture file will print sharper.',
    low_res_low:
      'At {size} this picture prints at {dpi} DPI, well under the {need} DPI we like, so it may look soft or blurry. A bigger picture file will print sharper.',
    remove_bg_button: 'Remove background',
    remove_bg_busy: 'Removing the background...',
    remove_bg_done: 'Background removed.',
    remove_bg_undo: 'Put my original back',
    remove_bg_undone: 'Your original picture is back.',
    remove_bg_failed: "We couldn't remove the background. Please try again.",
    preview_title: 'How it will look',
    preview_note: "Something doesn't look right? Contact us if you need a mockup, as this preview may not be exact.",
    turn_title: "Direction",
    turn_up: "Up the cup (sideways)",
    turn_across: "Straight across"
  };

  function pictureWord(cfg, key) {
    var words = cfg && cfg.picture_words;
    var v = words && words[key];
    return typeof v === 'string' && v.trim() ? v.trim() : PICTURE_WORDS[key];
  }

  function fillWords(text, values) {
    return String(text).replace(/\{(size|dpi|need)\}/g, function (whole, key) {
      return String(values[key]);
    });
  }

  function sizeWords(w, h) {
    return Math.round(w * 10) / 10 + ' by ' + Math.round(h * 10) / 10 + ' inches';
  }

  // A raster upload's reading: {dpi, need, ratio, band, wIn, hIn}, or null.
  function pictureQuality(upload, cfg) {
    var q = cfg && cfg.quality;
    if (!upload || !q) return null;
    if (upload.kind && upload.kind !== 'image') return null; // vector art and PDFs
    var w = Number(upload.width);
    var h = Number(upload.height);
    var need = Number(q.min_dpi);
    var wide = Number(q.width_in);
    var tall = Number(q.max_height_in);
    if (!(w > 0) || !(h > 0) || !(need > 0) || !(wide > 0) || !(tall > 0)) return null;
    var aspect = w / h;
    var pw = wide;
    var ph = wide / aspect;
    if (ph > tall + 1e-9) {
      ph = tall;
      pw = tall * aspect;
    }
    var dpi = w / pw;
    var ratio = dpi / need;
    var band = ratio >= 2 ? 'excellent' : ratio >= 1 ? 'good' : ratio >= 0.6 ? 'fair' : 'low';
    return { dpi: dpi, need: need, ratio: ratio, band: band, wIn: pw, hIn: ph };
  }

  // What the customer is told about a small picture, or null when it is fine.
  // The DPI reads down (149.6 is 149), as the ticket's does.
  function qualityNotice(upload, cfg) {
    var q = pictureQuality(upload, cfg);
    if (!q || (q.band !== 'fair' && q.band !== 'low')) return null;
    var low = q.band === 'low';
    return {
      band: q.band,
      title: pictureWord(cfg, 'quality_title'),
      word: pictureWord(cfg, low ? 'quality_low' : 'quality_fair'),
      text: fillWords(pictureWord(cfg, low ? 'low_res_low' : 'low_res_fair'), {
        size: sizeWords(q.wIn, q.hIn),
        dpi: Math.floor(q.dpi),
        need: q.need
      })
    };
  }

  // Remove background works on a picture with pixels: not vector art, not a PDF.
  function canRemoveBackground(upload) {
    return !!upload && (!upload.kind || upload.kind === 'image');
  }

  // A picked file, checked before it is sent (the server checks again).
  function fileProblem(file) {
    if (!file) return 'Please choose a file.';
    var name = file.name || 'That file';
    if (!FILE_EXT_RE.test(file.name || '') && FILE_TYPES.indexOf(file.type || '') < 0) {
      return name + " isn't a file we can use. Please choose a JPG, PNG, WebP, SVG, PDF or AI file.";
    }
    if (!file.size) return name + ' is empty. Please choose another file.';
    if (file.size > MAX_UPLOAD_BYTES) return name + ' is larger than 25 MB. Please choose a smaller file.';
    return '';
  }

  function needsRights(state) {
    return isUploadMethod(state.sides.front.method) || (!!state.back && isUploadMethod(state.sides.back.method));
  }

  // The image use agreement's words are the shop's own (the admin's Image
  // use agreement card, 2026-09-27), sent with the config as
  // words.artwork_rights. A missing or blank word keeps the shipped one
  // (this label, and the snippet's printed words).
  var RIGHTS_LABEL = 'Image use agreement';
  function rightsWord(words, key) {
    var w = words && words.artwork_rights;
    var v = w && w[key];
    return typeof v === 'string' && v.trim() ? v.trim() : '';
  }
  function rightsLabel(cfg) {
    return (cfg && typeof cfg.rights_label === 'string' && cfg.rights_label.trim()) ||
      RIGHTS_LABEL;
  }

  function sideProblems(side, word, cfg, out) {
    if (!side.method) {
      out.push({ field: word + '-method', message: 'Please choose what goes on the ' + word + '.' });
      return;
    }
    if (side.method === 'engraved_text') {
      textCheck(side.text, cfg.text, word).problems.forEach(function (message) {
        out.push({ field: word + '-text', message: message });
      });
      if (!side.font) out.push({ field: word + '-font', message: 'Please pick a font for the ' + word + '.' });
      return;
    }
    if (side.uploading || side.removing) {
      out.push({
        field: word + '-upload',
        message: 'Your ' + word + ' file is still uploading. Please wait a moment, then try again.'
      });
    } else if (!side.upload) {
      out.push({
        field: word + '-upload',
        message: (side.method === 'full_color' ? 'Please upload the image for the ' : 'Please upload the logo for the ') +
          word + '.'
      });
    }
  }

  function quantityProblem(qty, maxQty) {
    if (qty > maxQty) {
      return 'You can add up to ' + maxQty + ' tumblers at a time. For more, add them in two steps.';
    }
    return 'Please choose how many, from 1 to ' + maxQty + '.';
  }

  function stockProblem(color, stock) {
    if (stock <= 0) return color + ' is sold out. Please pick another color.';
    return (
      'We have only ' + stock + ' ' + color + ' ' + plural(stock, 'tumbler', 'tumblers') +
      ' left. Please choose ' + stock + ' or fewer.'
    );
  }

  // What is wrong with the count, in words, or ''. `typing` is the live check
  // while the box is being edited: an empty or half-typed box is not yet a
  // mistake, but more than the limit or more than the color has in stock is
  // said straight away.
  function quantityMessage(state, cfg, typing) {
    var maxQty = Number(cfg && cfg.max_qty) || 60;
    var qty = state.quantity;
    if (typing && !(qty >= 1)) return '';
    if (!(qty >= 1 && qty <= maxQty && Math.floor(qty) === qty)) return quantityProblem(qty, maxQty);
    if (state.color && typeof state.stock === 'number' && qty > state.stock) return stockProblem(state.color, state.stock);
    return '';
  }

  // Everything that stops an add, in form order: [{field, message}].
  function problems(state, cfg) {
    var out = [];
    if (!state.color) out.push({ field: 'color', message: 'Please pick a color.' });
    var qtyWords = quantityMessage(state, cfg, false);
    if (qtyWords) out.push({ field: 'quantity', message: qtyWords });
    sideProblems(state.sides.front, 'front', cfg, out);
    if (state.back) sideProblems(state.sides.back, 'back', cfg, out);
    if (needsRights(state) && !state.rights) {
      out.push({
        field: 'rights',
        message: 'Please tick the ' + rightsLabel(cfg) + ' box. It confirms you have the right to use this artwork.'
      });
    }
    return out;
  }

  // Which way an engraved side runs (owner 2026-10-07: "names engrave
  // sideways", "for the name and logo allow the option to rotate"): the
  // customer's pick, else words up the cup and logos straight across. A full
  // color (UV) side has none.
  function sideTurn(side) {
    if (side.method === 'engraved_text') return side.turn || 'up';
    if (side.method === 'engraved_logo') return side.turn || 'across';
    return '';
  }

  function sidePayload(side, cfg) {
    if (side.method === 'engraved_text') {
      return {
        method: 'engraved_text',
        text: textCheck(side.text, cfg && cfg.text).text,
        font: parseInt(side.font, 10),
        turn: sideTurn(side)
      };
    }
    var out = {
      method: side.method,
      upload: side.upload ? side.upload.key : '',
      filename: side.upload ? side.upload.filename : ''
    };
    if (side.method === 'engraved_logo') out.turn = sideTurn(side);
    return out;
  }

  // The door's request body (TUMBLER-SCOPE.md, "The door").
  function payload(state, cfg) {
    return {
      color: state.color,
      quantity: state.quantity,
      front: sidePayload(state.sides.front, cfg),
      back: state.back ? sidePayload(state.sides.back, cfg) : null,
      rights: needsRights(state) ? !!state.rights : false
    };
  }

  // THE LIVE CUP PREVIEW (owner 2026-10-07): the body POST /api/tumbler/preview
  // is sent for one side, or null while that side has nothing to show (no
  // way chosen, no words, no finished upload). The server draws it on the
  // real photo of the color with the order emails' own code.
  function previewBody(state, sideName, cfg) {
    var side = state.sides[sideName];
    if (!side || (sideName === 'back' && !state.back)) return null;
    if (side.method === 'engraved_text') {
      if (!textCheck(side.text, cfg && cfg.text).lines.length) return null;
    } else if (isUploadMethod(side.method)) {
      if (!side.upload || side.uploading || side.removing) return null;
    } else {
      return null;
    }
    return { color: state.color, side: sidePayload(side, cfg) };
  }

  // How many tumblers (not back sides) a /cart.js answer holds.
  function cartTumblerCount(cart) {
    var n = 0;
    ((cart && cart.items) || []).forEach(function (item) {
      var sku = String((item && item.sku) || '');
      if (sku.indexOf('TMB40-') === 0 && sku !== 'TMB40-BACK') n += Number(item.quantity) || 0;
    });
    return n;
  }

  // The words for a refusal from one of the app's doors: its `detail`.
  // `generic` is what to say when the door said nothing we can show.
  function doorError(status, body, generic) {
    var detail = body && body.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (Array.isArray(detail) && detail.length) {
      return detail
        .map(function (d) {
          return d && d.msg ? String(d.msg) : String(d);
        })
        .join(' ');
    }
    if (status === 429) return 'Too many tries in a short time. Please wait a minute, then try again.';
    if (status === 413) return 'That file is too large. Please choose a file under 25 MB.';
    if (generic) return generic;
    return (
      "We couldn't add your tumblers (error " + status +
      '). Please try again, or contact us if it keeps happening.'
    );
  }

  // The words for a refusal from Shopify's cart: its `description` or `message`.
  function shopifyError(status, body) {
    var said = body && (body.description || body.message || body.errors);
    if (typeof said === 'string' && said.trim()) return said.trim();
    return "Your cart couldn't take these tumblers (error " + status + '). Please try again.';
  }

  var logic = {
    money: money,
    toCents: toCents,
    sortedLadder: sortedLadder,
    rungIndex: rungIndex,
    rungLabel: rungLabel,
    priceLines: priceLines,
    textCheck: textCheck,
    textWords: textWords,
    chipColors: chipColors,
    fileProblem: fileProblem,
    pictureWord: pictureWord,
    fillWords: fillWords,
    pictureQuality: pictureQuality,
    qualityNotice: qualityNotice,
    canRemoveBackground: canRemoveBackground,
    needsRights: needsRights,
    rightsWord: rightsWord,
    rightsLabel: rightsLabel,
    quantityMessage: quantityMessage,
    problems: problems,
    payload: payload,
    sideTurn: sideTurn,
    previewBody: previewBody,
    cartTumblerCount: cartTumblerCount,
    doorError: doorError,
    shopifyError: shopifyError
  };

  if (typeof window === 'undefined') {
    if (typeof module === 'object' && module && module.exports) module.exports = logic;
    return;
  }
  window.MGTumblerForm = logic;

  // ------------------------------------------------------------ the page

  var NETWORK_PROBLEM = "We couldn't reach our server. Please check your connection and try again.";

  function newSide() {
    return {
      method: '',
      text: '',
      font: '',
      turn: '',
      upload: null,
      uploading: false,
      removing: false,
      original: null,
      uploadError: '',
      token: 0
    };
  }

  function readJson(response) {
    return response.text().then(function (text) {
      var body = null;
      try {
        body = text ? JSON.parse(text) : null;
      } catch (e) {
        body = null;
      }
      return { ok: response.ok, status: response.status, body: body };
    });
  }

  // An Error that carries the customer's words.
  function saying(words) {
    var err = new Error(words);
    err.mgSay = words;
    return err;
  }

  function offline() {
    throw saying(NETWORK_PROBLEM);
  }

  function boot(root) {
    if (root.getAttribute('data-mg-tmb-ready')) return;
    var form = root.querySelector('[data-mg-tmb-form]');
    if (!form) return;
    root.setAttribute('data-mg-tmb-ready', '1');

    var apiBase = root.getAttribute('data-api-base') || '/apps/design';

    function one(selector) {
      return root.querySelector(selector);
    }
    function all(selector) {
      return Array.prototype.slice.call(root.querySelectorAll(selector));
    }
    function sideEl(kind, side) {
      return root.querySelector('[data-mg-tmb-' + kind + '="' + side + '"]');
    }

    var cfg = readFallback();
    var state = {
      color: '',
      variantId: '',
      stock: null,
      quantity: 1,
      back: false,
      rights: false,
      inCart: 0,
      tried: false,
      sides: { front: newSide(), back: newSide() }
    };
    var busy = false;

    var qtyInput = one('[data-mg-tmb-qty]');
    var backBox = one('[data-mg-tmb-back]');
    var rightsBox = one('[data-mg-tmb-rights]');
    var addButton = one('[data-mg-tmb-add]');
    var addLabel = addButton ? addButton.textContent.trim() : 'Add to cart';
    var alertBox = one('[data-mg-tmb-alert]');
    var alertTitle = one('[data-mg-tmb-alert-title]');
    var alertList = one('[data-mg-tmb-alert-list]');
    var statusLine = one('[data-mg-tmb-status]');
    var chart = one('[data-mg-tmb-chart]');
    var chartOpener = null;

    function readFallback() {
      var node = root.querySelector('[data-mg-tmb-fallback]');
      var parsed = null;
      try {
        parsed = node ? JSON.parse(node.textContent) : null;
      } catch (e) {
        parsed = null;
      }
      parsed = parsed || {};
      return {
        ladder: parsed.ladder || [],
        back_side_price: parsed.back_side_price,
        max_qty: parsed.max_qty || 60,
        text: parsed.text || { max_lines: 3, max_chars: 40 },
        fonts: null,
        fallback: true
      };
    }

    // ---- reading the controls into state

    function readColor(input, fromCustomer) {
      state.color = input.value;
      state.variantId = input.getAttribute('data-variant-id') || '';
      var stock = input.getAttribute('data-stock');
      state.stock = stock === null || stock === '' ? null : parseInt(stock, 10);
      if (fromCustomer) showVariantMedia(input.getAttribute('data-media-id'));
    }

    function readQuantity() {
      var raw = String(qtyInput.value || '').trim();
      state.quantity = /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
    }

    function readAll() {
      var picked = one('input[name="mg-tmb-color"]:checked');
      if (picked && !picked.disabled) readColor(picked, false);
      readQuantity();
      state.back = !!(backBox && backBox.checked);
      state.rights = !!(rightsBox && rightsBox.checked);
      ['front', 'back'].forEach(function (side) {
        var s = state.sides[side];
        var method = root.querySelector('[data-mg-tmb-method="' + side + '"]:checked');
        s.method = method ? method.value : '';
        var text = sideEl('text', side);
        s.text = text ? text.value : '';
        var font = sideEl('font', side);
        s.font = font ? font.value : '';
      });
    }

    // ---- the gallery follows the color, but never yanks the page
    // Dawn's setActiveMedia scrolls the window up to the picture when it is
    // above the fold; a customer halfway down this form would lose their
    // place. So the photo only changes while the gallery is in view.
    function showVariantMedia(mediaId) {
      if (!mediaId) return;
      var info = root.closest('product-info');
      var gallery = info && info.querySelector('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function' || !info.dataset.section) return;
      if (gallery.getBoundingClientRect().top < 0) return;
      try {
        gallery.setActiveMedia(info.dataset.section + '-' + mediaId, true);
      } catch (e) {
        /* the photo is a nicety; the order is not */
      }
    }

    // ---- painting

    function setHidden(el, hidden) {
      if (el) el.hidden = !!hidden;
    }

    function paintSide(side) {
      var s = state.sides[side];
      var isText = s.method === 'engraved_text';
      var isUpload = isUploadMethod(s.method);
      setHidden(sideEl('panel', side + '-text'), !isText);
      setHidden(sideEl('panel', side + '-upload'), !isUpload);
      setHidden(sideEl('fullcolor-note', side), s.method !== 'full_color');

      var label = sideEl('file-label', side);
      if (label) label.textContent = s.method === 'full_color' ? 'Upload your image' : 'Upload your logo';

      var box = sideEl('filebox', side);
      var img = sideEl('preview', side);
      var name = sideEl('filename', side);
      if (s.upload) {
        setHidden(box, false);
        if (name) {
          name.textContent =
            'Uploaded: ' + s.upload.filename + (s.upload.kind === 'vector' ? ' (vector artwork, no preview shown)' : '');
        }
        if (img) {
          var src = s.upload.kind === 'vector' ? '' : apiBase + s.upload.url;
          if (src && img.getAttribute('src') !== src) {
            img.hidden = false;
            img.alt = 'Preview of ' + s.upload.filename;
            img.src = src;
          } else if (!src) {
            img.hidden = true;
            img.removeAttribute('src');
          }
        }
      } else {
        setHidden(box, true);
        if (img) {
          img.hidden = true;
          img.removeAttribute('src');
        }
      }

      paintPicture(side);
      paintCount(side);
    }

    // The Direction choice under an engraved side (words or logo).
    function paintTurn(side) {
      var box = sideEl('turnbox', side);
      if (!box) return;
      var s = state.sides[side];
      var turn = logic.sideTurn(s);
      box.hidden = !turn;
      var title = box.querySelector('[data-mg-tmb-turn-title]');
      if (title) title.textContent = logic.pictureWord(cfg, 'turn_title');
      ['up', 'across'].forEach(function (value) {
        var word = box.querySelector('[data-mg-tmb-turn-word="' + value + '"]');
        if (word) word.textContent = logic.pictureWord(cfg, value === 'up' ? 'turn_up' : 'turn_across');
        var input = box.querySelector('input[value="' + value + '"]');
        if (input) input.checked = turn === value;
      });
    }

    // The live cup preview under each side: asked for when what that side
    // shows changes (color, way, words, font, file), a moment after the
    // customer stops typing, and an older answer never paints over a newer.
    var cup = {
      front: { said: '', token: 0, timer: 0, url: '' },
      back: { said: '', token: 0, timer: 0, url: '' }
    };

    function paintCup(side) {
      var box = sideEl('cup', side);
      if (!box) return;
      var c = cup[side];
      var body = logic.previewBody(state, side, cfg);
      var title = box.querySelector('[data-mg-tmb-cup-title]');
      var note = box.querySelector('[data-mg-tmb-cup-note]');
      if (title) title.textContent = logic.pictureWord(cfg, 'preview_title');
      if (note) {
        // the shop's words, with "Contact us" (any case) turned into a link
        var words = logic.pictureWord(cfg, 'preview_note');
        var at = words.toLowerCase().indexOf('contact us');
        note.textContent = '';
        if (at < 0) {
          note.textContent = words;
        } else {
          var link = document.createElement('a');
          link.href = '/pages/contact';
          link.textContent = words.slice(at, at + 10);
          note.appendChild(document.createTextNode(words.slice(0, at)));
          note.appendChild(link);
          note.appendChild(document.createTextNode(words.slice(at + 10)));
        }
      }
      var said = body ? JSON.stringify(body) : '';
      if (said === c.said) return;
      c.said = said;
      clearTimeout(c.timer);
      c.token += 1;
      if (!body) {
        box.hidden = true;
        return;
      }
      var token = c.token;
      box.setAttribute('aria-busy', 'true');
      c.timer = setTimeout(function () {
        fetch(apiBase + '/api/tumbler/preview', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: said
        })
          .then(function (res) {
            if (!res.ok) throw new Error('preview ' + res.status);
            return res.blob();
          })
          .then(function (blob) {
            if (token !== c.token) return;
            var img = box.querySelector('[data-mg-tmb-cup-img]');
            if (c.url) URL.revokeObjectURL(c.url);
            c.url = URL.createObjectURL(blob);
            if (img) {
              img.src = c.url;
              img.alt = logic.pictureWord(cfg, 'preview_title') + ': ' + state.color;
            }
            box.hidden = false;
            box.removeAttribute('aria-busy');
          })
          .catch(function () {
            // the preview is a nicety: a miss keeps the last picture (or none)
            if (token === c.token) box.removeAttribute('aria-busy');
          });
      }, 450);
    }

    // Under an uploaded picture: its print quality notice, and the Remove
    // background and Put my original back buttons. All the words are the
    // shop's own (cfg.picture_words).
    function paintPicture(side) {
      var s = state.sides[side];
      var notice = s.upload && !s.removing ? logic.qualityNotice(s.upload, cfg) : null;
      var qbox = sideEl('quality', side);
      if (qbox) {
        qbox.textContent = '';
        qbox.className = 'mg-tmb__quality';
        if (notice) {
          qbox.className = 'mg-tmb__quality mg-tmb__quality--' + notice.band;
          var head = document.createElement('strong');
          head.className = 'mg-tmb__quality-head';
          head.textContent = notice.title + ': ' + notice.word;
          var body = document.createElement('span');
          body.className = 'mg-tmb__quality-text';
          body.textContent = notice.text;
          qbox.appendChild(head);
          qbox.appendChild(body);
        }
        qbox.hidden = !notice;
      }
      var cut = sideEl('removebg', side);
      if (cut) {
        cut.textContent = logic.pictureWord(cfg, 'remove_bg_button');
        cut.disabled = !!s.removing;
        cut.hidden = !(cfg.remove_bg && !s.uploading && logic.canRemoveBackground(s.upload));
      }
      var undo = sideEl('bgundo', side);
      if (undo) {
        undo.textContent = logic.pictureWord(cfg, 'remove_bg_undo');
        undo.hidden = !(s.original && !s.removing && !s.uploading);
      }
    }

    function paintCount(side) {
      var box = sideEl('count', side);
      if (!box) return;
      var check = logic.textCheck(state.sides[side].text, cfg.text);
      box.textContent = '';
      if (!check.lines.length) {
        var empty = document.createElement('span');
        empty.textContent = 'Up to ' + check.maxLines + ' lines, ' + check.maxChars + ' characters a line.';
        box.appendChild(empty);
        return;
      }
      check.counts.forEach(function (n, k) {
        var row = document.createElement('span');
        row.className = 'mg-tmb__count-line' + (n > check.maxChars || k >= check.maxLines ? ' is-over' : '');
        row.textContent =
          'Line ' + (k + 1) + ': ' + n + ' of ' + check.maxChars + ' characters' +
          (n > check.maxChars ? ' (too long)' : '') +
          (k >= check.maxLines ? ' (only ' + check.maxLines + ' lines fit)' : '');
        box.appendChild(row);
      });
    }

    function paintSame() {
      var button = one('[data-mg-tmb-same]');
      if (!button) return;
      var front = state.sides.front;
      var back = state.sides.back;
      var show =
        state.back &&
        isUploadMethod(back.method) &&
        isUploadMethod(front.method) &&
        !!front.upload &&
        !front.uploading &&
        !(back.upload && back.upload.key === front.upload.key);
      button.hidden = !show;
    }

    function paintColors() {
      all('.mg-tmb__color').forEach(function (label) {
        var input = label.querySelector('input');
        label.classList.toggle('is-selected', !!(input && input.checked));
      });
    }

    function paintMethods() {
      all('.mg-tmb__method').forEach(function (label) {
        var input = label.querySelector('input');
        label.classList.toggle('is-selected', !!(input && input.checked));
      });
    }

    function paintStepper() {
      var max = Number(cfg.max_qty) || 60;
      var q = state.quantity;
      all('[data-mg-tmb-step]').forEach(function (button) {
        var dir = parseInt(button.getAttribute('data-mg-tmb-step'), 10);
        button.disabled = q >= 1 && ((dir < 0 && q <= 1) || (dir > 0 && q >= max));
      });
    }

    function paintPrice() {
      var lines = logic.priceLines(cfg, state.quantity, state.back, state.inCart);
      function line(name, text) {
        var el = one('[data-mg-tmb-line="' + name + '"]');
        if (!el) return;
        el.textContent = text;
        if (name !== 'tumblers' && name !== 'total') el.hidden = !text;
      }
      if (!lines.tumblers) {
        line('tumblers', 'Choose how many to see your price.');
        line('back', '');
        line('total', '');
        line('cart', '');
        line('next', '');
      } else {
        line('tumblers', lines.tumblers);
        line('back', lines.back);
        line('total', lines.total);
        line('cart', lines.cart);
        line('next', lines.next);
      }
      all('[data-mg-tmb-ladder-body] tr').forEach(function (row) {
        var current = String(lines.rung) === row.getAttribute('data-rung');
        row.classList.toggle('is-current', current);
        var mark = row.querySelector('[data-mg-tmb-yours]');
        if (mark) mark.hidden = !current;
      });
    }

    // A refused upload stays said until the customer picks another file,
    // removes it, or stops using an upload on that side.
    function uploadErrors() {
      var out = [];
      ['front', 'back'].forEach(function (side) {
        var s = state.sides[side];
        var used = isUploadMethod(s.method) && (side === 'front' || state.back);
        if (used && s.uploadError) out.push({ field: side + '-upload', message: s.uploadError });
      });
      return out;
    }

    function paint() {
      paintColors();
      paintMethods();
      paintSide('front');
      paintSide('back');
      paintTurn('front');
      paintTurn('back');
      paintCup('front');
      paintCup('back');
      setHidden(one('[data-mg-tmb-side="back"]'), !state.back);
      setHidden(one('[data-mg-tmb-rights-box]'), !logic.needsRights(state));
      paintSame();
      paintStepper();
      paintPrice();
      if (state.tried) {
        var list = logic.problems(state, cfg);
        showFieldErrors(uploadErrors().concat(list));
        // The summary follows the fixes, but is only rebuilt when its words
        // change, so a screen reader is not read the alert on every keystroke.
        if (alertBox && !alertBox.hidden && alertBox.getAttribute('data-kind') === 'check') {
          if (!list.length) hideAlert();
          else if (saidOf(list) !== alertBox.getAttribute('data-said')) {
            fillAlert(list, 'Please fix these before adding to cart:');
          }
        }
      } else {
        // Before the first try only what is already certain is said: a count
        // over the limit or over the color's stock, and a refused upload.
        var live = logic.quantityMessage(state, cfg, true);
        showFieldErrors(uploadErrors().concat(live ? [{ field: 'quantity', message: live }] : []));
      }
    }

    // ---- errors in words, next to each field

    function controlFor(field) {
      if (field === 'color') {
        return one('input[name="mg-tmb-color"]:checked') || one('input[name="mg-tmb-color"]:not(:disabled)');
      }
      if (field === 'quantity') return qtyInput;
      if (field === 'rights') return rightsBox;
      var parts = field.split('-');
      var side = parts[0];
      var kind = parts[1];
      if (kind === 'method') return one('[data-mg-tmb-method="' + side + '"]');
      if (kind === 'text') return sideEl('text', side);
      if (kind === 'font') return sideEl('fontbutton', side) || sideEl('font', side);
      if (kind === 'upload') return sideEl('file', side);
      return null;
    }

    function showFieldErrors(list) {
      var byField = {};
      list.forEach(function (p) {
        (byField[p.field] = byField[p.field] || []).push(p.message);
      });
      all('[data-mg-tmb-error]').forEach(function (el) {
        var field = el.getAttribute('data-mg-tmb-error');
        var messages = byField[field] || [];
        el.textContent = messages.join(' ');
        el.hidden = !messages.length;
        var control = controlFor(field);
        if (control) {
          if (messages.length) control.setAttribute('aria-invalid', 'true');
          else control.removeAttribute('aria-invalid');
        }
      });
    }

    function saidOf(list) {
      return list
        .map(function (p) {
          return p.message;
        })
        .join('|');
    }

    function fillAlert(list, title) {
      if (!alertBox) return;
      alertBox.setAttribute('data-said', saidOf(list));
      alertTitle.textContent = title || '';
      alertTitle.hidden = !title;
      alertList.textContent = '';
      list.forEach(function (p) {
        var item = document.createElement('li');
        var control = p.field ? controlFor(p.field) : null;
        if (control && control.id) {
          var link = document.createElement('a');
          link.href = '#' + control.id;
          link.textContent = p.message;
          link.addEventListener('click', function (event) {
            event.preventDefault();
            if (control.scrollIntoView) control.scrollIntoView({ block: 'center' });
            control.focus();
          });
          item.appendChild(link);
        } else {
          item.textContent = p.message;
        }
        alertList.appendChild(item);
      });
    }

    function showAlert(list, title, kind) {
      if (!alertBox) return;
      alertBox.setAttribute('data-kind', kind || 'server');
      alertBox.hidden = false;
      fillAlert(list, title);
      try {
        alertBox.focus({ preventScroll: true });
      } catch (e) {
        alertBox.focus();
      }
      if (alertBox.scrollIntoView) alertBox.scrollIntoView({ block: 'center' });
    }

    function hideAlert() {
      if (!alertBox) return;
      alertBox.hidden = true;
      alertBox.removeAttribute('data-kind');
      alertBox.removeAttribute('data-said');
      alertList.textContent = '';
    }

    function setStatus(text) {
      if (statusLine) statusLine.textContent = text || '';
    }

    // ---- uploads: sent the moment a file is picked

    function setUploadStatus(side, text) {
      var el = sideEl('upload-status', side);
      if (el) el.textContent = text || '';
    }

    function uploadRefused(side, words) {
      state.sides[side].uploadError = words;
      paint();
    }

    function upload(side, file) {
      var s = state.sides[side];
      var input = sideEl('file', side);
      var refused = logic.fileProblem(file);
      s.uploadError = '';
      if (refused) {
        if (input) input.value = '';
        uploadRefused(side, refused);
        return;
      }
      var token = ++s.token;
      var name = file.name || 'your file';
      s.uploading = true;
      s.removing = false;
      s.original = null;
      s.upload = null;
      setUploadStatus(side, 'Uploading ' + name + '...');
      paint();
      // &trim=1: the see-through margins are cut off when the picture is
      // stored, as the designers ask, so the pixel size the quality notice
      // reads is the art's own, the same box the production ticket measures.
      fetch(apiBase + '/api/upload?name=' + encodeURIComponent(name.slice(0, 80)) + '&trim=1', {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
        credentials: 'same-origin'
      })
        .then(readJson, offline)
        .then(function (res) {
          if (!res.ok) {
            throw saying(
              "We couldn't upload " + name + ': ' +
              logic.doorError(res.status, res.body, 'error ' + res.status + '. Please try again.')
            );
          }
          if (!res.body || !res.body.key || !res.body.url) {
            throw saying("We couldn't upload " + name + '. Please try again.');
          }
          if (token !== s.token) return;
          s.uploading = false;
          s.upload = {
            key: res.body.key,
            url: res.body.url,
            filename: name,
            kind: res.body.kind || '',
            width: res.body.width,
            height: res.body.height
          };
          setUploadStatus(side, '');
          paint();
        })
        .catch(function (err) {
          if (token !== s.token) return;
          s.uploading = false;
          s.upload = null;
          if (input) input.value = '';
          setUploadStatus(side, '');
          uploadRefused(side, err && err.mgSay ? err.mgSay : "We couldn't upload " + name + '. Please try again.');
          if (!(err && err.mgSay)) console.error('[mg-tumbler] upload failed', err);
        });
    }

    function removeFile(side) {
      var s = state.sides[side];
      s.token += 1; // an upload still in flight lands nowhere
      s.uploading = false;
      s.removing = false;
      s.original = null;
      s.upload = null;
      s.uploadError = '';
      var input = sideEl('file', side);
      if (input) input.value = '';
      setUploadStatus(side, 'File removed.');
      paint();
    }

    // ---- Remove background: the same POST /api/ai/remove-bg every designer
    // calls. The server cuts the background out of the stored picture (kept
    // under a new key, auto-cropped to what is left) and answers {key, url,
    // width, height}. The picture the customer uploaded stays in storage and
    // one button puts it back; the side is not orderable while it is working.

    function removeBackground(side) {
      var s = state.sides[side];
      if (!s.upload || s.uploading || s.removing || !logic.canRemoveBackground(s.upload)) return;
      var before = s.upload;
      var token = ++s.token;
      s.removing = true;
      s.uploadError = '';
      setUploadStatus(side, logic.pictureWord(cfg, 'remove_bg_busy'));
      paint();
      fetch(apiBase + '/api/ai/remove-bg?key=' + encodeURIComponent(before.key), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(readJson, offline)
        .then(function (res) {
          if (!res.ok) {
            throw saying(logic.doorError(res.status, res.body, logic.pictureWord(cfg, 'remove_bg_failed')));
          }
          if (!res.body || !res.body.key || !res.body.url) {
            throw saying(logic.pictureWord(cfg, 'remove_bg_failed'));
          }
          if (token !== s.token) return;
          s.removing = false;
          s.original = s.original || before;
          s.upload = {
            key: res.body.key,
            url: res.body.url,
            filename: before.filename,
            kind: '',
            width: res.body.width,
            height: res.body.height
          };
          setUploadStatus(side, logic.pictureWord(cfg, 'remove_bg_done'));
          paint();
        })
        .catch(function (err) {
          if (token !== s.token) return;
          s.removing = false;
          setUploadStatus(side, '');
          uploadRefused(side, err && err.mgSay ? err.mgSay : logic.pictureWord(cfg, 'remove_bg_failed'));
          if (!(err && err.mgSay)) console.error('[mg-tumbler] remove background failed', err);
        });
    }

    function putOriginalBack(side) {
      var s = state.sides[side];
      if (!s.original || s.removing || s.uploading) return;
      s.token += 1;
      s.upload = s.original;
      s.original = null;
      s.uploadError = '';
      setUploadStatus(side, logic.pictureWord(cfg, 'remove_bg_undone'));
      paint();
    }

    // ---- config and cart count

    function applyConfig() {
      all('[data-mg-tmb-back-price]').forEach(function (el) {
        el.textContent = logic.money(logic.toCents(cfg.back_side_price));
      });
      var max = Number(cfg.max_qty) || 60;
      if (qtyInput) qtyInput.max = String(max);
      all('[data-mg-tmb-max]').forEach(function (el) {
        el.textContent = String(max);
      });
      applyFonts(cfg.fonts);
      applyWords();
      applyChips(cfg.color_hex);
      buildLadder();
    }

    // The words that count lines, characters and fonts, once the config
    // has answered (the fallback keeps the snippet's own words).
    function applyWords() {
      if (cfg.fallback) return;
      var words = logic.textWords(cfg.text, Array.isArray(cfg.fonts) ? cfg.fonts.length : 0);
      if (!words) return;
      all('[data-mg-tmb-text-about]').forEach(function (el) {
        el.textContent = words.about;
      });
      all('[data-mg-tmb-text-rule]').forEach(function (el) {
        el.textContent = words.rule;
      });
      all('textarea[data-mg-tmb-text]').forEach(function (el) {
        el.rows = Math.min(Math.max(Number(cfg.text.max_lines) || 3, 1), 6);
      });
    }

    // A chip color the owner set in the app, painted over the theme's own.
    function applyChips(map) {
      var chips = logic.chipColors(map);
      all('.mg-tmb__color').forEach(function (label) {
        var input = label.querySelector('input');
        var chip = label.querySelector('.mg-tmb__chip');
        if (input && chip && chips[input.value]) chip.style.background = chips[input.value];
      });
    }

    function applyFonts(fonts) {
      if (!Array.isArray(fonts) || !fonts.length) return;
      // The number AND the name, the name drawn in its own font (owner
      // 2026-10-07: "show the font name using the font", "also show 1 2 3";
      // this replaces the 2026-09-24 numbers-only list).
      var clean = fonts
        .filter(function (f) {
          return f && f.n !== undefined && f.n !== null;
        })
        .map(function (f) {
          var name = String(f.name || '');
          return { value: String(f.n), label: String(f.n) + (name ? ' ' + name : ''), name: name };
        });
      if (!clean.length) return;
      var wanted = clean.map(function (f) { return f.value + '|' + f.label; }).join(',');
      all('[data-mg-tmb-font]').forEach(function (select) {
        var have = Array.prototype.slice
          .call(select.options, 1)
          .map(function (o) { return o.value + '|' + o.textContent.trim(); })
          .join(',');
        if (have === wanted) return;
        var keep = select.value;
        while (select.options.length > 1) select.remove(1);
        clean.forEach(function (f) {
          var option = document.createElement('option');
          option.value = f.value;
          option.textContent = f.label;
          option.setAttribute('data-name', f.name);
          select.appendChild(option);
        });
        select.value = keep;
        if (select.value !== keep) select.value = '';
        state.sides[select.getAttribute('data-mg-tmb-font')].font = select.value;
      });
      startingFont();
      buildFontPicks();
    }

    // The starting font (the admin's Starting font, owner 2026-10-07: "make
    // the default font 2"), until the customer picks one themselves.
    function startingFont() {
      var want = cfg && cfg.default_font !== undefined && cfg.default_font !== null ? String(cfg.default_font) : '';
      if (!want) return;
      all('[data-mg-tmb-font]').forEach(function (select) {
        if (select.getAttribute('data-picked') === '1') return;
        var had = select.value;
        select.value = want;
        if (select.value !== want) select.value = had;
        state.sides[select.getAttribute('data-mg-tmb-font')].font = select.value;
      });
    }

    // ---- the font list: each font's number and its name drawn in the font

    function fontFamily(name) {
      return name ? '"MGT ' + name.replace(/"/g, '') + '", cursive' : '';
    }

    function fontOptions(select) {
      return Array.prototype.slice.call(select.options).filter(function (o) {
        return o.value !== '';
      });
    }

    function buildFontPicks() {
      ['front', 'back'].forEach(function (side) {
        var select = sideEl('font', side);
        var list = sideEl('fontlist', side);
        if (!select || !list) return;
        list.textContent = '';
        fontOptions(select).forEach(function (o) {
          var name = o.getAttribute('data-name') || '';
          var item = document.createElement('li');
          item.className = 'mg-tmb__fontpick-option';
          item.id = 'mg-tmb-' + side + '-font-' + o.value;
          item.setAttribute('role', 'option');
          item.setAttribute('tabindex', '-1');
          item.setAttribute('data-value', o.value);
          var num = document.createElement('span');
          num.className = 'mg-tmb__fontpick-num';
          num.textContent = o.value;
          var shown = document.createElement('span');
          shown.className = 'mg-tmb__fontpick-name';
          shown.textContent = name || 'Font ' + o.value;
          shown.style.fontFamily = fontFamily(name);
          item.appendChild(num);
          item.appendChild(shown);
          item.addEventListener('click', function () {
            chooseFont(side, o.value);
          });
          list.appendChild(item);
        });
        paintFontPick(side);
      });
    }

    function paintFontPick(side) {
      var select = sideEl('font', side);
      var button = sideEl('fontbutton', side);
      var list = sideEl('fontlist', side);
      if (!select || !button) return;
      var picked = fontOptions(select).filter(function (o) { return o.value === select.value; })[0];
      var num = button.querySelector('[data-mg-tmb-fontpick-num]');
      var name = button.querySelector('[data-mg-tmb-fontpick-name]');
      var label = picked ? picked.getAttribute('data-name') || 'Font ' + picked.value : '';
      if (num) {
        num.textContent = picked ? picked.value : '';
        num.hidden = !picked;
      }
      if (name) {
        name.textContent = picked ? label : 'Pick a font';
        name.style.fontFamily = picked ? fontFamily(picked.getAttribute('data-name') || '') : '';
      }
      if (list) {
        Array.prototype.forEach.call(list.children, function (item) {
          item.setAttribute('aria-selected', item.getAttribute('data-value') === select.value ? 'true' : 'false');
        });
      }
    }

    function openFontList(side, focusFirst) {
      var button = sideEl('fontbutton', side);
      var list = sideEl('fontlist', side);
      var select = sideEl('font', side);
      if (!button || !list) return;
      ['front', 'back'].forEach(function (other) {
        if (other !== side) closeFontList(other, false);
      });
      list.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      var at = list.querySelector('[data-value="' + (select ? select.value : '') + '"]') || list.firstElementChild;
      if (focusFirst && at) at.focus();
      if (at && at.scrollIntoView) at.scrollIntoView({ block: 'nearest' });
    }

    function closeFontList(side, refocus) {
      var button = sideEl('fontbutton', side);
      var list = sideEl('fontlist', side);
      if (!button || !list || list.hidden) return;
      list.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      if (refocus) button.focus();
    }

    function chooseFont(side, value) {
      var select = sideEl('font', side);
      if (!select) return;
      select.value = value;
      select.setAttribute('data-picked', '1');
      select.dispatchEvent(new Event('change', { bubbles: true }));
      closeFontList(side, true);
    }

    function buildLadder() {
      var table = one('[data-mg-tmb-ladder]');
      var body = one('[data-mg-tmb-ladder-body]');
      var ladder = logic.sortedLadder(cfg.ladder);
      if (!table || !body) return;
      if (cfg.fallback || ladder.length < 2) {
        table.hidden = true;
        return;
      }
      body.textContent = '';
      ladder.forEach(function (r, i) {
        var row = document.createElement('tr');
        row.setAttribute('data-rung', String(i));
        var head = document.createElement('th');
        head.scope = 'row';
        head.textContent = logic.rungLabel(r);
        var cell = document.createElement('td');
        cell.appendChild(document.createTextNode(logic.money(logic.toCents(r.unit))));
        var yours = document.createElement('span');
        yours.className = 'mg-tmb__yours';
        yours.setAttribute('data-mg-tmb-yours', '');
        yours.textContent = ' (your price)';
        yours.hidden = true;
        cell.appendChild(yours);
        row.appendChild(head);
        row.appendChild(cell);
        body.appendChild(row);
      });
      table.hidden = false;
    }

    // The shop's own image use agreement (words.artwork_rights), over the
    // shipped words the snippet prints. A word the server did not send
    // stays as printed.
    function applyRightsWords(words) {
      var label = logic.rightsWord(words, 'label');
      var text = logic.rightsWord(words, 'text');
      var labelEl = one('[data-mg-tmb-rights-label]');
      var textEl = one('#mg-tmb-rights-text');
      if (label && labelEl) labelEl.textContent = label;
      if (text && textEl) textEl.textContent = text;
    }

    function loadConfig() {
      fetch(apiBase + '/api/tumbler/config', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(readJson)
        .then(function (res) {
          if (!res.ok || !res.body || !logic.sortedLadder(res.body.ladder).length) return;
          var b = res.body;
          cfg = {
            ladder: b.ladder,
            back_side_price: b.back_side_price !== undefined && b.back_side_price !== null ? b.back_side_price : cfg.back_side_price,
            max_qty: Number(b.max_qty) || cfg.max_qty,
            text: b.text && b.text.max_lines ? b.text : cfg.text,
            fonts: Array.isArray(b.fonts) ? b.fonts : null,
            color_hex: b.color_hex && typeof b.color_hex === 'object' ? b.color_hex : null,
            rights_label: logic.rightsWord(b.words, 'label'),
            quality: b.quality && typeof b.quality === 'object' ? b.quality : null,
            picture_words: b.picture_words && typeof b.picture_words === 'object' ? b.picture_words : null,
            remove_bg: b.remove_bg === true,
            fallback: false
          };
          applyRightsWords(b.words);
          applyConfig();
          paint();
        })
        .catch(function () {
          /* The listing's own price stays on show and the cart applies the
             group price, so a missing price list costs the customer nothing. */
        });
    }

    function loadCartCount() {
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (cart) {
          if (!cart) return;
          state.inCart = logic.cartTumblerCount(cart);
          paintPrice();
        })
        .catch(function () {
          /* without the count the price line shows these tumblers alone */
        });
    }

    // ---- add to cart

    function setBusy(on) {
      busy = on;
      if (!addButton) return;
      addButton.disabled = on;
      addButton.setAttribute('aria-busy', on ? 'true' : 'false');
      addButton.textContent = on ? 'Adding...' : addLabel;
    }

    function addToCart(items) {
      var drawer = document.querySelector('cart-drawer');
      var request = { items: items };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        request.sections = drawer
          .getSectionsToRender()
          .map(function (section) {
            return section.id;
          })
          .join(',');
        request.sections_url = window.location.pathname;
      }
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(request),
        credentials: 'same-origin'
      })
        .then(readJson, offline)
        .then(function (res) {
          var said = res.body && typeof res.body.status === 'number' && res.body.status >= 400;
          if (!res.ok || said) throw saying(logic.shopifyError(res.status, res.body));
          return res.body || {};
        });
    }

    function added(cart) {
      // The choices stay (a second color with the same name is one click
      // away); the form just stops marking fields until the next try.
      state.tried = false;
      paint();
      setStatus('Added to your cart.');
      loadCartCount();
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && cart && cart.sections) {
        try {
          if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(addButton);
          drawer.classList.remove('is-empty');
          drawer.renderContents(cart);
          // Dawn's pubsub: PUB_SUB_EVENTS is a global `const`, so it is
          // reached by name, never as window.PUB_SUB_EVENTS.
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mg-tumbler-form', cartData: cart });
          }
          return;
        } catch (e) {
          console.error('[mg-tumbler] cart drawer did not open', e);
        }
      }
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }

    function add() {
      if (busy) return;
      readAll();
      state.tried = true;
      var list = logic.problems(state, cfg);
      showFieldErrors(uploadErrors().concat(list));
      if (list.length) {
        showAlert(list, 'Please fix these before adding to cart:', 'check');
        return;
      }
      hideAlert();
      setStatus('');
      setBusy(true);
      fetch(apiBase + '/api/cart/tumbler', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(logic.payload(state, cfg)),
        credentials: 'same-origin'
      })
        .then(readJson, offline)
        .then(function (res) {
          if (!res.ok) throw saying(logic.doorError(res.status, res.body));
          var items = res.body && res.body.items;
          if (!Array.isArray(items) || !items.length) {
            throw saying("We couldn't add your tumblers: our server sent back an empty answer. Please try again.");
          }
          return addToCart(items);
        })
        .then(added)
        .catch(function (err) {
          var words = err && err.mgSay ? err.mgSay : 'Something went wrong adding your tumblers. Please try again.';
          if (!(err && err.mgSay)) console.error('[mg-tumbler] add to cart failed', err);
          showAlert([{ field: '', message: words }], '', 'server');
        })
        .then(function () {
          setBusy(false);
        });
    }

    // ---- the font chart

    function openChart(opener) {
      if (!chart) return;
      chartOpener = opener;
      if (typeof chart.showModal === 'function') chart.showModal();
      else chart.setAttribute('open', '');
      var close = chart.querySelector('[data-mg-tmb-chart-close]');
      if (close) close.focus();
    }

    function closeChart() {
      if (!chart) return;
      if (typeof chart.close === 'function' && chart.open) {
        chart.close(); // its 'close' event puts focus back on the opener
      } else {
        chart.removeAttribute('open');
        if (chartOpener) chartOpener.focus();
      }
    }

    // ---- wiring

    all('input[name="mg-tmb-color"]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) readColor(input, true);
        paint();
      });
    });

    if (qtyInput) {
      qtyInput.addEventListener('input', function () {
        readQuantity();
        paint();
      });
    }

    all('[data-mg-tmb-step]').forEach(function (button) {
      button.addEventListener('click', function () {
        var max = Number(cfg.max_qty) || 60;
        var dir = parseInt(button.getAttribute('data-mg-tmb-step'), 10);
        var q = state.quantity >= 1 ? state.quantity : 0;
        var next = Math.min(max, Math.max(1, q + dir));
        qtyInput.value = String(next);
        readQuantity();
        paint();
      });
    });

    all('[data-mg-tmb-method]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) state.sides[input.getAttribute('data-mg-tmb-method')].method = input.value;
        paint();
      });
    });

    all('[data-mg-tmb-text]').forEach(function (area) {
      area.addEventListener('input', function () {
        state.sides[area.getAttribute('data-mg-tmb-text')].text = area.value;
        paint();
      });
    });

    all('[data-mg-tmb-font]').forEach(function (select) {
      select.addEventListener('change', function () {
        state.sides[select.getAttribute('data-mg-tmb-font')].font = select.value;
        paintFontPick(select.getAttribute('data-mg-tmb-font'));
        paint();
      });
    });

    all('[data-mg-tmb-turn]').forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) state.sides[input.getAttribute('data-mg-tmb-turn')].turn = input.value;
        paint();
      });
    });

    // The font list's button and options: click, and the keys a listbox
    // takes (arrows, Home, End, Enter or Space to pick, Escape to close).
    all('[data-mg-tmb-fontbutton]').forEach(function (button) {
      var side = button.getAttribute('data-mg-tmb-fontbutton');
      button.addEventListener('click', function () {
        var list = sideEl('fontlist', side);
        if (list && !list.hidden) closeFontList(side, false);
        else openFontList(side, true);
      });
      button.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          openFontList(side, true);
        }
      });
    });
    all('[data-mg-tmb-fontlist]').forEach(function (list) {
      var side = list.getAttribute('data-mg-tmb-fontlist');
      list.addEventListener('keydown', function (event) {
        var items = Array.prototype.slice.call(list.children);
        var at = items.indexOf(document.activeElement);
        var go = null;
        if (event.key === 'ArrowDown') go = Math.min(items.length - 1, at + 1);
        else if (event.key === 'ArrowUp') go = Math.max(0, at - 1);
        else if (event.key === 'Home') go = 0;
        else if (event.key === 'End') go = items.length - 1;
        else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (at >= 0) chooseFont(side, items[at].getAttribute('data-value'));
          return;
        } else if (event.key === 'Escape') {
          event.preventDefault();
          closeFontList(side, true);
          return;
        } else if (event.key === 'Tab') {
          closeFontList(side, false);
          return;
        }
        if (go !== null && items[go]) {
          event.preventDefault();
          items[go].focus();
        }
      });
    });
    if (document.addEventListener) document.addEventListener('click', function (event) {
      ['front', 'back'].forEach(function (side) {
        var wrap = sideEl('fontpick', side);
        if (wrap && !wrap.contains(event.target)) closeFontList(side, false);
      });
    });
    buildFontPicks();

    all('[data-mg-tmb-file]').forEach(function (input) {
      input.addEventListener('change', function () {
        var side = input.getAttribute('data-mg-tmb-file');
        if (input.files && input.files[0]) upload(side, input.files[0]);
      });
    });

    // The file is stored either way; a preview that will not draw just goes.
    all('[data-mg-tmb-preview]').forEach(function (img) {
      img.addEventListener('error', function () {
        img.hidden = true;
      });
    });

    all('[data-mg-tmb-remove-file]').forEach(function (button) {
      button.addEventListener('click', function () {
        removeFile(button.getAttribute('data-mg-tmb-remove-file'));
      });
    });

    all('[data-mg-tmb-removebg]').forEach(function (button) {
      button.addEventListener('click', function () {
        removeBackground(button.getAttribute('data-mg-tmb-removebg'));
      });
    });

    all('[data-mg-tmb-bgundo]').forEach(function (button) {
      button.addEventListener('click', function () {
        putOriginalBack(button.getAttribute('data-mg-tmb-bgundo'));
      });
    });

    var same = one('[data-mg-tmb-same]');
    if (same) {
      same.addEventListener('click', function () {
        var front = state.sides.front.upload;
        if (!front) return;
        var back = state.sides.back;
        back.token += 1;
        back.uploading = false;
        back.removing = false;
        back.original = null;
        back.uploadError = '';
        back.upload = {
          key: front.key,
          url: front.url,
          filename: front.filename,
          kind: front.kind,
          width: front.width,
          height: front.height
        };
        setUploadStatus('back', 'Using the same image as the front.');
        paint();
      });
    }

    if (backBox) {
      backBox.addEventListener('change', function () {
        state.back = backBox.checked;
        paint();
      });
    }

    if (rightsBox) {
      rightsBox.addEventListener('change', function () {
        state.rights = rightsBox.checked;
        paint();
      });
    }

    var rightsMore = one('[data-mg-tmb-rights-more]');
    if (rightsMore) {
      rightsMore.addEventListener('click', function () {
        var text = one('#mg-tmb-rights-text');
        var open = rightsMore.getAttribute('aria-expanded') === 'true';
        rightsMore.setAttribute('aria-expanded', open ? 'false' : 'true');
        rightsMore.textContent = open ? 'Read it' : 'Hide';
        setHidden(text, open);
      });
    }

    all('[data-mg-tmb-chart-open]').forEach(function (button) {
      button.addEventListener('click', function () {
        openChart(button);
      });
    });
    if (chart) {
      var closer = chart.querySelector('[data-mg-tmb-chart-close]');
      if (closer) closer.addEventListener('click', closeChart);
      chart.addEventListener('click', function (event) {
        if (event.target === chart) closeChart(); // a tap on the dimmed backdrop
      });
      chart.addEventListener('close', function () {
        if (chartOpener) chartOpener.focus();
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      add();
    });

    // ---- start: the script is here, so the form can do its job
    readAll();
    applyConfig();
    paint();
    setHidden(one('[data-mg-tmb-boot]'), true);
    form.hidden = false;
    loadConfig();
    loadCartCount();
  }

  Array.prototype.forEach.call(document.querySelectorAll('[data-mg-tmb]'), boot);
})();
